// One building: stage, dates and links; its contracts & agreements (incl. shared ones) with documents and claims;
// registers; smaller sections (defects, drawings & documents, follow-ups, inspections); details; close-out download.
import { CopyText } from '../ui/CopyText';
import { toVip, vatOn } from '../lib/vat';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { write } from '../lib/api';
import { money } from '../lib/claimCalc';
import { fmtDate, splitList, stageLabel, STAGES } from '../lib/format';
import { STAGE_LOOK, STAGE_STEPS, contractsFor, type Building, type Contract, type Project } from '../lib/buildings';
import { Button, Empty, errText, useToast } from '../ui/kit';
import { Facts, type FactSpec } from '../ui/item/Facts';
import { ContractEditor } from '../ui/building/ContractEditor';
import { ContractsCard, progressOf, useContractClaims } from '../ui/building/ContractsCard';
import { ReadFile, useLatestRead } from '../ui/building/ReadFile';
import { MoreSections, RegisterTiles, ReviewList, type BItem } from '../ui/building/Sections';
import { ProgrammeSection } from '../ui/building/Programme';

const NONE = '00000000-0000-0000-0000-000000000000';
type Org = { id: string; name: string; kind: string; short_code: string | null };

const addMonths = (iso: string, m: number) => {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCMonth(d.getUTCMonth() + m);
  return d.toISOString().slice(0, 10);
};

export default function BuildingPage() {
  const { id = '' } = useParams();
  const { siteId, can, session } = useApp();
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);

  const bs = useRows<Building>(() => supabase.from('buildings').select('*').eq('site_id', siteId ?? NONE).order('sort').order('name'), [siteId], ['buildings']);
  const projects = useRows<Project>(() => supabase.from('projects').select('id, name, code, status, sort, deleted_at').eq('site_id', siteId ?? NONE), [siteId], ['projects']);
  const cs = useRows<Contract>(() => supabase.from('contracts').select('*').eq('site_id', siteId ?? NONE).is('deleted_at', null), [siteId], ['contracts']);
  const orgs = useRows<Org>(() => supabase.from('organisations').select('id, name, kind, short_code').is('deleted_at', null).order('name'), [], ['organisations']);
  const its = useRows<BItem>(
    () => supabase.from('items').select('id, type, number, title, status, amount, respond_by, onus_org_id, review').contains('building_ids', [id]).is('deleted_at', null),
    [id],
    ['items'],
  );

  const b = bs.rows.find((x) => x.id === id) ?? null;
  const contracts = useMemo(() => (b ? contractsFor(b, cs.rows, bs.rows) : []), [b, cs.rows, bs.rows]);
  const claims = useContractClaims(contracts.filter((c) => c.has_claims).map((c) => c.id));
  const names = useMemo(() => new Map(bs.rows.map((x) => [x.id, x.short_code ? `${x.short_code}` : x.name])), [bs.rows]);
  const approvedVar = useMemo(() => its.rows.filter((i) => i.type === 'variation' && (i.status === 'approved' || i.status === 'complete')).reduce((s, i) => s + Number(i.amount ?? 0), 0), [its.rows]);
  const { call, reload: reloadCall } = useLatestRead(id);

  const [editor, setEditor] = useState<{ c: Contract | null } | null>(null);
  const [reading, setReading] = useState(false);
  const [zip, setZip] = useState<string | null>(null);
  const [zipAll, setZipAll] = useState(false);
  const [docsVersion, setDocsVersion] = useState(0);

  const editB = can('buildings', 4);
  const editC = can('contracts', 4);
  const seeC = can('contracts', 1);

  if (bs.loading) return <Empty>Loading…</Empty>;
  if (!b) return <Empty>Building not found. <Link to="/buildings">Back to Buildings &amp; projects</Link></Empty>;

  const project = projects.rows.find((p) => p.id === b.project_id);
  const own = contracts.filter((c) => c.has_claims && c.building_ids.includes(b.id));
  const sum = own.reduce((s, c) => s + Number(c.sum_vep ?? 0), 0);
  const sumVip = own.reduce((s, c) => s + toVip(Number(c.sum_vep ?? 0), vatOn(c, c.signed_on)), 0);
  const vatNow = own.length === 1 ? vatOn(own[0]) : vatOn(null);
  const prog = own.map((c) => progressOf(c, claims, approvedVar));
  const gross = prog.reduce((s, p) => s + p.toDate.gross_vep, 0);
  const ret = prog.reduce((s, p) => s + p.toDate.retention_held, 0);
  const pct = sum + approvedVar > 0 ? (gross / (sum + approvedVar)) * 100 : 0;
  const dlp = own.find((c) => c.dlp_months)?.dlp_months;
  const pc = b.revised_pc_date ?? b.pc_date;
  const look = STAGE_LOOK[b.stage] ?? STAGE_LOOK.design;
  const stageI = STAGE_STEPS.findIndex((s) => s.value === b.stage);

  async function saveB(values: Record<string, unknown>, summary: string) {
    try {
      await write('buildings', 'update', { id: b!.id, values: { ...values, _expect_updated_at: b!.updated_at }, summary });
      await bs.reload();
      return true;
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
      return false;
    }
  }

  async function closeout() {
    setZip('Starting…');
    try {
      const orgNames = new Map(orgs.rows.map((o) => [o.id, o.name]));
      const { buildCloseout } = await import('../ui/building/closeout');
      const r = await buildCloseout(b!, seeC ? contracts : [], orgNames, { itemAttachments: zipAll }, setZip);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.blob);
      a.download = r.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 30000);
      toast({ tone: r.failed.length ? 'error' : 'ok', text: r.failed.length ? `Downloaded — ${r.failed.length} file(s) could not be added (listed in README.txt)` : `Close-out pack downloaded (${r.files} files)` });
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setZip(null);
    }
  }

  const facts: FactSpec[] = [
    { key: 'name', label: 'Name', type: 'text', value: b.name, display: b.name },
    { key: 'short_code', label: 'Short code', type: 'text', value: b.short_code, display: b.short_code ?? '—' },
    { key: 'project_id', label: 'Project', type: 'select', value: b.project_id, display: project?.name ?? '—', options: projects.rows.filter((p) => !p.deleted_at).map((p) => ({ value: p.id, label: p.name })) },
    { key: 'aliases', label: 'Other names (match imports & emails)', type: 'text', value: (b.aliases ?? []).join(', '), display: b.aliases?.length ? b.aliases.join(', ') : '—', wide: true },
    { key: 'stage', label: 'Stage', type: 'select', value: b.stage, display: stageLabel(b.stage), options: STAGES },
    { key: 'pc_date', label: 'PC date', type: 'date', value: b.pc_date, display: fmtDate(b.pc_date) },
    { key: 'revised_pc_date', label: 'Revised PC date (after EOTs)', type: 'date', value: b.revised_pc_date, display: fmtDate(b.revised_pc_date) },
    { key: 'drive_folder_url', label: 'Google Drive folder', type: 'text', value: b.drive_folder_url, display: b.drive_folder_url ? 'Set — opens in Drive' : '—', wide: true },
    { key: 'photos_url', label: 'Google Photos album', type: 'text', value: b.photos_url, display: b.photos_url ? 'Set — opens in Google Photos' : '—', wide: true },
    { key: 'notes', label: 'Notes', type: 'textarea', value: b.notes, display: b.notes ?? '—', wide: true },
  ];

  return (
    <div className="bpage">
      <div className="bhead">
        <div className="small muted">
          <Link to="/buildings">Buildings &amp; projects</Link> › {project?.name ?? 'No project'} › {b.name}
        </div>
        <div className="bhead-top">
          <span className="btile" aria-hidden="true">
            {(b.short_code ?? b.name.slice(0, 3)).slice(0, 5)}
          </span>
          <div className="grow">
            <h1>{b.name}</h1>
            <div className="bhead-sub">
              {b.short_code ? (
                <span className="bref">
                  Building code <CopyText value={b.short_code} label="building code" />
                </span>
              ) : null}
              {own.map((c) => (
                <span key={c.id} className="bref">
                  {own.length > 1 ? `${c.title ?? 'Contract'} · ` : ''}Client ref <CopyText value={c.contract_no} label="client ref" />
                  {c.project_no ? (
                    <>
                      {' '}
                      · contractor's ref <CopyText value={c.project_no} label="contractor's ref" />
                    </>
                  ) : null}
                </span>
              ))}
              {b.aliases?.length ? (
                <>
                  Also matched as{' '}
                  {b.aliases.map((a) => (
                    <span key={a} className="chip-s">
                      {a}
                    </span>
                  ))}
                </>
              ) : null}
              {b.drive_folder_url ? (
                <a href={b.drive_folder_url} target="_blank" rel="noopener noreferrer">
                  Drive folder ↗
                </a>
              ) : null}
              {b.photos_url ? (
                <a href={b.photos_url} target="_blank" rel="noopener noreferrer">
                  Google Photos album ↗
                </a>
              ) : null}
            </div>
          </div>
          <div className="bhead-actions">
            {editB ? (
              <Button kind="primary" onClick={() => setReading(true)}>
                Upload a file — Claude reads it
              </Button>
            ) : null}
            {editC ? <Button onClick={() => setEditor({ c: null })}>+ Contract</Button> : null}
            <Button disabled={Boolean(zip)} onClick={() => void closeout()}>
              {zip ?? 'Download close-out pack'}
            </Button>
          </div>
        </div>
        <div className="bhead-bottom">
          {b.stage === 'on_hold' ? (
            <span className="bstg" style={{ background: look.tint, color: look.color }}>
              On hold
            </span>
          ) : (
            <ol className="bstages" aria-label="Stage">
              {STAGE_STEPS.map((s, i) => {
                const cls = i < stageI || b.stage === 'complete' ? 'done' : i === stageI ? 'cur' : 'todo';
                return (
                  <li key={s.value} className={cls}>
                    <button type="button" disabled={!editB} aria-current={i === stageI ? 'step' : undefined} aria-label={`Set stage ${s.label}`} onClick={() => void saveB({ stage: s.value }, `Stage → ${s.label}`)}>
                      <span className="bar" aria-hidden="true" />
                      <span className="dot">{cls === 'done' ? '✓' : i + 1}</span>
                      <span className="lbl">{s.label}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
          <div className="bdates">
            <div>
              <span className="k">PC date</span>
              <b>{fmtDate(b.pc_date)}</b>
            </div>
            <div>
              <span className="k">Revised PC</span>
              <b className={b.revised_pc_date ? 'orange-t' : ''}>{fmtDate(b.revised_pc_date)}</b>
            </div>
            <div>
              <span className="k">DLP ends</span>
              <b>{pc && dlp ? fmtDate(addMonths(pc, dlp)) : '—'}</b>
            </div>
          </div>
        </div>
      </div>

      <label className="check small zip-opt">
        <input type="checkbox" checked={zipAll} onChange={(e) => setZipAll(e.target.checked)} /> Close-out pack: also include the attachments of RFIs and notices (larger download)
      </label>

      {call && !call.confirmed_at && call.phase !== 'failed' ? (
        <div className={`banner ${call.phase === 'done' ? 'banner-claude' : 'banner-info'}`}>
          {call.phase === 'running' ? 'Claude is reading a file for this building…' : 'Claude has read a file for this building — check what it found.'}
          {call.phase === 'done' && editB ? (
            <Button kind="primary" onClick={() => setReading(true)}>
              Check &amp; save
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="bgrid">
        <div className="bmain">
          {seeC && own.length ? (
            <div className="money-strip bstrip">
              <div className="money">
                <span>{own.length === 1 ? 'Building contract · VEP' : `${own.length} contracts · VEP`}</span>
                <b>{sum ? money(sum) : '—'}</b>
                <span className="vip-line">{sum ? `${money(sumVip)} VIP` : ''}</span>
                <span>{own.length === 1 ? <>budget no. <CopyText value={own[0].contract_no} label="budget no." /></> : 'claims run against these'}</span>
              </div>
              <div className="money">
                <span>Approved variations · VEP</span>
                <b>{money(approvedVar)}</b>
                <span className="vip-line">{money(toVip(approvedVar, vatNow))} VIP</span>
                <span>from Variations &amp; Requests</span>
              </div>
              <div className="money">
                <span>Certified to date · VEP</span>
                <b className="good-t">{money(gross)}</b>
                <span className="vip-line">{money(toVip(gross, vatNow))} VIP</span>
                <span>{Math.round(pct)} % of contract + variations</span>
              </div>
              <div className="money">
                <span>Retention held · FJD</span>
                <b>{money(ret)}</b>
                <span className="vip-line">VEP · {money(toVip(ret, vatNow))} VIP</span>
                <span>after the last certified claim</span>
              </div>
              <div className="money">
                <span>Still to certify · VEP</span>
                <b>{money(Math.max(0, sum + approvedVar - gross))}</b>
                <span className="vip-line">{money(toVip(Math.max(0, sum + approvedVar - gross), vatNow))} VIP</span>
                <span>contract + variations − certified</span>
              </div>
            </div>
          ) : null}

          {seeC ? (
            <ContractsCard
              building={b}
              contracts={contracts}
              claims={claims}
              approvedVar={approvedVar}
              orgs={orgs.rows}
              names={names}
              siteId={siteId!}
              canEdit={editC}
              onEdit={(c) => setEditor({ c })}
              onAdd={() => setEditor({ c: null })}
              onRead={() => setReading(true)}
            />
          ) : null}

          <ProgrammeSection
            building={b}
            canEdit={editB && can('files', 4)}
            siteId={siteId!}
            version={docsVersion}
            onReading={() => (void reloadCall(), setReading(true))}
            onBuildingChanged={() => (void bs.reload(), setDocsVersion((v) => v + 1))}
          />
          <RegisterTiles buildingId={b.id} items={its.rows} today={today} />
          <MoreSections uid={session?.user.id ?? 'anon'} buildingId={b.id} items={its.rows} today={today} docsVersion={docsVersion} canEdit={editB && can('files', 4)} />
        </div>

        <aside className="brail">
          <section className="card">
            <div className="card-head">
              <h2>Building details</h2>
              {editB ? <span className="muted small on-dark">click to edit</span> : null}
            </div>
            <div className="card-body">
              <Facts
                facts={facts}
                canEdit={editB}
                onSave={(key, value, label) => {
                  let v: unknown = value;
                  if (key === 'aliases') v = splitList(String(value ?? ''));
                  if ((key === 'drive_folder_url' || key === 'photos_url') && v && !/^https:\/\//i.test(String(v))) {
                    toast({ tone: 'error', text: 'Paste the full link, starting with https://' });
                    return Promise.resolve(false);
                  }
                  if (key === 'name' && !String(v ?? '').trim()) return Promise.resolve(false);
                  return saveB({ [key]: v === '' ? null : v }, `Changed ${label}`);
                }}
              />
              <p className="muted small" style={{ margin: '8px 0 0' }}>
                Google Photos albums open in Google Photos — Google no longer lets other apps show albums inside them.
              </p>
            </div>
          </section>
          <ReviewList items={its.rows} />
        </aside>
      </div>

      {editor ? (
        <ContractEditor
          open
          contract={editor.c}
          building={b}
          buildings={bs.rows}
          orgs={orgs.rows}
          siteId={siteId!}
          canEdit={editC}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            void cs.reload();
          }}
        />
      ) : null}
      {reading ? (
        <ReadFile
          open
          onClose={() => setReading(false)}
          building={b}
          contracts={contracts}
          orgs={orgs.rows}
          siteId={siteId!}
          call={call}
          reloadCall={reloadCall}
          onSaved={() => {
            void bs.reload();
            void cs.reload();
            setDocsVersion((v) => v + 1);
          }}
        />
      ) : null}
    </div>
  );
}
